import { SessionContext } from '@contexts/session-context';
import { LicensePoolType } from '@enums/license-pool-type.enum';
import { ResultConstant } from '@enums/result-constant.enum';
import { ExecutionTriggeredType } from '@enums/triggered-type.enum';
import { User } from '@models/user.model';
import TestDeviceIconGroup from '@modules/test-development/components/plans/list/test-device-icon-group';
import RunResultDurationComponent from '@modules/test-development/components/run-results/list/run-result-duration-component';
import UserActivity from '@modules/test-development/components/user-activity';
import { StatusConstant } from '@modules/test-development/enums/status-constant.enum';
import { ExecutionResult } from '@modules/test-development/models/execution-result.model';
import {
  DebuggerUsageDetails,
  TestPlanLicenseUsage,
  TestPlanRunType,
} from '@modules/test-development/models/license-usage.model';
import { TestDevice } from '@modules/test-development/models/test-device.model';
import { MobileInspectionStatus } from '@modules/test-development/pages/mobile-inspector/enums/mobile-inspection-status.enum';
import { MobileInspection } from '@modules/test-development/pages/mobile-inspector/models/mobile-inspection.model';
import MobileInspectionService from '@modules/test-development/pages/mobile-inspector/services/mobile-inspections';
import { ExecutionResultService } from '@services/execution-result.service';
import { TestCaseResultService } from '@services/test-case-result.service';
import { UserService } from '@services/user.service';
import { TestCaseResult } from '@testsigma/organism';
import {
  DateTimeFormat,
  Icon,
  ToolTip,
  TruncatedText,
  toastify,
  useAnimatedClass,
  useTranslation,
} from '@testsigma/atoms';
import classNames from 'classnames';
import { useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Agent } from '@models/agent.model';
import { ReportAgentButton } from '@modules/agent/report-agent';
import { AgentService } from '@services/agent.service';

export const TestPlanList = (text: string) => {
  const { t } = useTranslation();

  const { session } = useContext(SessionContext);
  const { user, tenantPreference, tenant } = session;
  const isAdminAccess: boolean = user?.isAdmin || user?.isSuperAdmin;
  const animationClass = useAnimatedClass('reval-animation');
  const navigate = useNavigate();

  const testplanItemGenerator = useCallback(
    (
      testPlanLicenseUsageList: TestPlanLicenseUsage[],
      refresh: () => void,
      onClose: (isClose: boolean) => void,
      onClickReportAgent?: (
        testPlanId: string | number,
      ) => (e: React.MouseEvent) => void,
      testPlanIdVsAgents?: Record<string, Agent[]>,
    ) => {
      const returnData = [];

      const naviagteToResult = (testPlanLicenseUsage: TestPlanLicenseUsage) => {
        return testPlanLicenseUsage?.isDry()
          ? `/td/dry_run/${testPlanLicenseUsage?.adhocTestCaseResultId}/step_results`
          : `/td/results/${testPlanLicenseUsage?.testPlanId}/runs/${testPlanLicenseUsage?.testPlanResultId}`;
      };

      testPlanLicenseUsageList.map((testPlanLicenseUsage) => {
        const { testPlanId } = testPlanLicenseUsage;
        const agentsFound = testPlanIdVsAgents[testPlanId]?.length > 0;

        const resultItem = [
          {
            className: '!text-primary-600',
            childDom: (
              <>
                {/* {(executionResult?.environment?.isDebugMode ||
                  executionResult?.environment?.executionEnvironment
                    ?.runInWebDebug) && (
                  <ToolTip content={t("debugging.running_debug_mode")}>
                    <Icon className="mr-2.5 text-error-700" name="bug" />
                  </ToolTip>
                )} */}
                <Link
                  reloadDocument
                  onClick={() => onClose(false)}
                  to={naviagteToResult(testPlanLicenseUsage)}
                  className={classNames(
                    'cursor-pointer flex flex-col h-full w-[200px] pr-5',
                    animationClass,
                  )}>
                  <TruncatedText
                    value={
                      testPlanLicenseUsage?.isDry()
                        ? 'Adhoc Run'
                        : testPlanLicenseUsage?.testPlanName
                    }
                    toolTipPlacement='top'
                    className='text-small leading-5'
                  />
                  {Boolean(testPlanLicenseUsage?.buildNumber) && (
                    <div className='basis-full text-small leading-5 mt-1'>
                      <TruncatedText
                        value={t('usage_details.build_no', {
                          BuildNo: testPlanLicenseUsage?.buildNumber,
                        })}
                        toolTipPlacement='bottom'
                        className='block basis-full !text-small !leading-5 !mt-1 !text-primary-600'
                      />
                    </div>
                  )}
                  {Boolean(!testPlanLicenseUsage?.buildNumber) && (
                    <div className='basis-full text-small leading-5 mt-1 '>
                      <TruncatedText
                        value={t('usage_details.Run_no', {
                          RunId: testPlanLicenseUsage?.isDry()
                            ? testPlanLicenseUsage?.adhocTestCaseResultId
                            : testPlanLicenseUsage?.testPlanResultId,
                        })}
                        toolTipPlacement='bottom'
                        className='block basis-full !text-small !leading-5 !mt-1 !text-primary-600'
                      />
                    </div>
                  )}
                </Link>
              </>
            ),
            width: 16,
          },
          {
            className: '!justify-start',
            childDom: (
              <>
                <div>{testPlanLicenseUsage?.parallelCount}</div>
              </>
            ),
            width: 4,
          },
          {
            childDom: (
              <>
                <TestDeviceIconGroup
                  environments={testPlanLicenseUsage?.testDevices}
                  testPlanId={testPlanId}
                  variant='labs'
                />
              </>
            ),
            width: 8,
          },
          {
            childDom: (
              <>
                <span>
                  {testPlanLicenseUsage?.isDry() ? 'Dry Run' : 'Test Plan'}
                </span>
              </>
            ),
            width: 5,
          },
          {
            childDom: (
              <>
                <UserActivity
                  avatarClassName='!h-6 !w-6 !text-xxs !leading-3 font-semibold'
                  nameClassName='!font-normal'
                  className='mr-3 w-full'
                  user={testPlanLicenseUsage?.executedBy}
                  tooltipPlacement='top-start'
                />
              </>
            ),
            width: 16,
          },
          {
            className: '!block',
            childDom: (
              <>
                <RunResultDurationComponent
                  startTime={testPlanLicenseUsage?.startTime || new Date()}
                  duration={
                    new Date().getTime() -
                    ((testPlanLicenseUsage?.startTime as unknown as number) ||
                      new Date().getTime())
                  }
                  dateFormat={'hh:mm a, d MMM, yyyy'}
                  className={'flex-wrap-reverse'}
                />
              </>
            ),
            width: 15,
          },
          {
            childDom: (
              <>
                <div className='basis-full truncate'>
                  <span>
                    <ToolTip
                      content={t(
                        'debugging_session.testcase_location.project',
                        {
                          projectName: testPlanLicenseUsage?.projectName,
                        },
                      )}>
                      {testPlanLicenseUsage?.projectName}
                    </ToolTip>
                  </span>
                </div>

                {testPlanLicenseUsage?.applicationName && (
                  <div className='basis-full flex overflow-hidden max-w-[140px]'>
                    {testPlanLicenseUsage?.applicationName && (
                      <span className='basis-8/12 truncate'>
                        <ToolTip
                        placement='top'
                        tooltipClassName='break-all'
                          content={t(
                            'debugging_session.testcase_location.app',
                            {
                              applicationName:
                                testPlanLicenseUsage?.applicationName,
                            },
                          )}>
                          {testPlanLicenseUsage?.applicationName}
                        </ToolTip>
                      </span>
                    )}

                    {testPlanLicenseUsage?.versionName && (
                      <span className='truncate basis-4/12'>
                        <ToolTip
                          placement='top'
                          tooltipClassName='break-all'
                          content={t(
                            'debugging_session.testcase_location.version',
                            { versionName: testPlanLicenseUsage?.versionName },
                          )}>
                          {testPlanLicenseUsage?.versionName}
                        </ToolTip>
                      </span>
                    )}
                  </div>
                )}
              </>
            ),
            width: 13,
          },
          {
            childDom: (
              <div className='flex'>
                <div
                  onClick={() =>
                    stopExecutionById(
                      testPlanLicenseUsage?.testPlanResultId,
                    ).then(() => {
                      toastify(t('run.stopped'), {
                        type: 'success',
                        position: 'top-center',
                      });
                      refresh?.();
                    })
                  }
                  className={classNames(
                    'px-2.5 py-1.5 pr-3 border border-secondary-200 rounded-md text-2xs leading-4 flex items-center min-w-fit hover:bg-error-100 hover:border-error-100 font-semibold',
                    {
                      '!opacity-50 !pointer-events-none':
                        tenantPreference.controlExecutionStopAction &&
                        !(
                          isAdminAccess ||
                          testPlanLicenseUsage?.executedBy?.id === user.id
                        ),
                    },
                  )}>
                  <Icon
                    name={'stop-circle-outline'}
                    className={'pr-1 text-error-600 text-base'}
                  />
                  {t('common.stop')}
                </div>

                {agentsFound && (
                  <ReportAgentButton
                    onClick={onClickReportAgent(testPlanId)}
                    className='w-5 h-5 flex justify-center ml-8'
                  />
                )}
              </div>
            ),
            width: 5,
          },
        ];
        returnData.push(resultItem);
      });

      return returnData;
    },
    [text],
  );

  const mobileInspactionGenerator = useCallback(
    (plans, refresh) => {
      const returnData = [];

      plans.map((mobileInspection: MobileInspection) => {
        const testDevice = new TestDevice();
        testDevice.executionLabType = mobileInspection.executionLabType;
        const resultItem = [
          {
            className: '!text-primary-600',
            childDom: (
              <>
                <UserActivity
                  avatarClassName='!h-6 !w-6 !text-xxs !leading-3 font-semibold'
                  nameClassName='!font-normal'
                  className='mr-3 w-full'
                  user={mobileInspection?.executedBy}
                  tooltipPlacement='top-start'
                />
              </>
            ),
            width: 35,
          },
          {
            className: '!block',
            childDom: (
              <>
                <DateTimeFormat
                  date={mobileInspection?.['createdAt'] || new Date()}
                  format={'h:mm a MMM d, yyyy'}
                  relative={false}
                />
              </>
            ),
            width: 30,
          },
          {
            childDom: (
              <>
                <TestDeviceIconGroup
                  environments={[testDevice]}
                  variant='labs'
                />
              </>
            ),
            width: 14,
          },
          {
            childDom: (
              <>
                <div
                  onClick={() =>
                    handleStopMobileInspection(
                      mobileInspection.id,
                      mobileInspection.sessionId,
                      refresh,
                    )
                  }
                  className='px-2.5 py-1.5 pr-3 border border-secondary-200 rounded-md text-2xs leading-4 flex items-center min-w-fit hover:bg-error-100 hover:border-error-100 font-semibold'>
                  <Icon
                    name={'stop-circle-outline'}
                    className={'text-base pr-1 text-error-600'}
                  />
                  {t('common.stop')}
                </div>
              </>
            ),
            width: 9,
          },
        ];
        returnData.push(resultItem);
      });

      return returnData;
    },
    [text],
  );

  const debuggingSessionGenerator = useCallback(
    (
      debuggingSessionUsageList: DebuggerUsageDetails[],
      refresh: () => void,
      onClose: (isClose: boolean) => void,
    ) => {
      const returnData = [];

      debuggingSessionUsageList.map((debuggingSessionUsage) => {
        const resultItem = [
          {
            className: '!text-primary-600',
            childDom: (
              <TruncatedText
                value={debuggingSessionUsage?.dryRunTestCaseName}
                toolTipPlacement='top'
                className='text-small leading-5'
              />
            ),
            width: 30,
          },
          {
            childDom: (
              <TestDeviceIconGroup
                environments={debuggingSessionUsage?.testDevices}
                variant='labs'
                labTypeVariant='detail'
                hideLaptypeIcon={true}
              />
            ),
            width: 12,
          },
          {
            childDom: (
              <>
                <UserActivity
                  avatarClassName='!h-6 !w-6 !text-xxs !leading-3 font-semibold'
                  nameClassName='!font-normal'
                  className='mr-3 w-full'
                  user={debuggingSessionUsage?.executedBy}
                  tooltipPlacement='top-start'
                />
              </>
            ),
            width: 15,
          },
          {
            className: '!block',
            childDom: (
              <>
                <RunResultDurationComponent
                  startTime={debuggingSessionUsage?.startTime || new Date()}
                  duration={
                    new Date().getTime() -
                    ((debuggingSessionUsage?.startTime as unknown as number) ||
                      new Date().getTime())
                  }
                  dateFormat={'hh:mm a, d MMM, yyyy'}
                  className={'flex-wrap-reverse'}
                />
              </>
            ),
            width: 15,
          },
          {
            childDom: (
              <>
                <div className='basis-full truncate'>
                  <span>
                    <ToolTip
                      content={t(
                        'debugging_session.testcase_location.project',
                        { projectName: debuggingSessionUsage?.projectName },
                      )}>
                      {debuggingSessionUsage?.projectName}
                    </ToolTip>
                  </span>
                </div>

                {debuggingSessionUsage?.applicationName && (
                  <div className='basis-full flex overflow-hidden max-w-[140px]'>
                    {debuggingSessionUsage?.applicationName && (
                      <span className='basis-9/12 truncate'>
                        <ToolTip
                          content={t(
                            'debugging_session.testcase_location.app',
                            {
                              applicationName:
                                debuggingSessionUsage?.applicationName,
                            },
                          )}>
                          {debuggingSessionUsage?.applicationName}
                        </ToolTip>
                      </span>
                    )}

                    {debuggingSessionUsage?.versionName && (
                      <span className='truncate'>
                        <ToolTip
                          content={t(
                            'debugging_session.testcase_location.version',
                            { versionName: debuggingSessionUsage?.versionName },
                          )}>
                          {debuggingSessionUsage?.versionName}
                        </ToolTip>
                      </span>
                    )}
                  </div>
                )}
              </>
            ),
            width: 15,
          },
          {
            childDom: (
              <>
                <div
                  onClick={() =>
                    handleStopDebugginSession(
                      debuggingSessionUsage?.testPlanResultId,
                      debuggingSessionUsage?.adhocTestCaseResultId,
                      refresh,
                    )
                  }
                  className={classNames(
                    'px-2.5 py-1.5 pr-3 border border-secondary-200 rounded-md text-2xs leading-4 text-secondary-unique-600 flex items-center min-w-fit hover:bg-error-100 hover:border-error-100 font-semibold',
                    {
                      '!opacity-50 !pointer-events-none':
                        tenantPreference.controlExecutionStopAction &&
                        !(
                          isAdminAccess ||
                          debuggingSessionUsage?.executedBy?.id === user.id
                        ),
                    },
                  )}>
                  <Icon
                    name={'stop-circle-outline'}
                    className={'pr-1 text-error-600 text-base'}
                  />
                  {t('common.stop')}
                </div>
              </>
            ),
            width: 5,
          },
        ];
        returnData.push(resultItem);
      });
      return returnData;
    },
    [text],
  );

  const handleStopMobileInspection = (
    inspectionId,
    sessionId,
    refresh,
    isDebug = false,
  ) => {
    const mobileInspection = {
      id: inspectionId,
      status: 'FINISHED',
      stoppedUserId: session.user.id,
    };

    MobileInspectionService.show(mobileInspection.id).then(
      (device: MobileInspection) => {
        sessionId = device.sessionId;

        MobileInspectionService.update(mobileInspection.id, mobileInspection)
          .then(
            () => {
              handleStopSuccess(isDebug, sessionId);
              toastify(
                t(
                  isDebug
                    ? 'debugging.mobile_inspections.stop.success'
                    : 'usage_details.mobile_inspections.stop.success',
                ),
                { type: 'success' },
              );
            },
            () => {
              handleStopFailure(isDebug);
              toastify(
                isDebug
                  ? 'debugging.mobile_inspections.stop.failure'
                  : 'usage_details.mobile_inspections.stop.failure',
                { type: 'error' },
              );
            },
          )
          .finally(() => refresh?.());
      },
    );

    // add return statement if needed
  };

  const handleStopDebugginSession = async (
    testPlanResultId,
    testCaseResultId,
    refresh,
  ) => {

    try {
      const testcaseResult = await TestCaseResultService.findById(testCaseResultId);
      if(testcaseResult?.environmentResult?.executionEnvironment?.runInMobileDebug) {
          handleStopMobileInspection(
            testcaseResult?.environmentResult?.mobileInspectionId,
            null,
            refresh,
            true,
          );
        } else {
          // only to close web debugger driver.
          await AgentService.stopDebuggerDriver(tenant?.id,testCaseResultId);
        }
      } catch (err) {
        console.log("Failed to fetch test case result: ", err);
      } finally {
        stopExecutionById(testPlanResultId).then(() => {
          toastify(t('run.stopped'), {
            type: 'success',
            position: 'top-center',
          });
          refresh?.();
        });
      }
  };

  const handleStopSuccess = (isDebug, sessionId) => {
    deleteSession(
      sessionId,
      t(
        isDebug
          ? 'debugging.mobile_inspections.stop.success'
          : 'usage_details.mobile_inspections.stop.success',
      ),
    );
  };

  const handleStopFailure = (isDebug) => {
    // this.translate
    //   .get(
    //     isDebug
    //       ? "debugging.mobile_inspections.stop.failure"
    //       : "usage_details.mobile_inspections.stop.failure"
    //   )
    //   .subscribe((res) => {
    //     this.showNotification("error", res);
    //   });
  };

  const deleteSession = (sessionId, successMessage) => {
    // DriverSessionsService.
    // this.http
    //   .delete(this.localAgentUrl + "/agent/api/v2/session_actions/" + sessionId)
    //   .pipe(
    //     map((result) => result),
    //     catchError((err) => {
    //       return "Problem while Deleting session";
    //     })
    //   )
    //   .subscribe({
    //     (result) => {
    //       toastify(successMessage, { type: "success" });
    //       this.dialogRef.close({ launch: true });
    //     },
    //     (err) => {
    //       toastify(successMessage, { type: "error" });
    //       this.showNotification("error", err);
    //     },
    //   });
  };

  const stopExecution = (executionResult: ExecutionResult) => {
    executionResult.result = ResultConstant.STOPPED;
    executionResult.status = StatusConstant.STATUS_COMPLETED;
    executionResult.canStopAll =
      executionResult.triggeredType == ExecutionTriggeredType.MANUAL;
    return new Promise((resolve, reject) => {
      ExecutionResultService.stop(executionResult.id).then(
        (res) => {
          toastify(t('run.stopped'), {
            type: 'success',
            position: 'top-center',
          });
          resolve(res);
        },
        (e) => {
          toastify(t('run.stop-failed'), {
            type: 'error',
            position: 'top-center',
          });
          reject(e);
        },
      );
    });
  };

  const stopExecutionById = (executionResultId: number) => {
    return ExecutionResultService.stop(executionResultId);
  };

  // Function to get the count of non-manual execution runs
  const getDryCount = useCallback(
    (executionResults: ExecutionResult[]): number => {
      let dryCount = 0;
      executionResults.forEach((er: ExecutionResult) => {
        if (!er.environmentResultsDTO[0].isHybrid) {
          dryCount += er.totalRunningCount;
        }
      });
      return dryCount;
    },
    [],
  );

  // Function to get the count of mobile runs for a specific user
  const getMobileRunsCount = useCallback(
    (mobileRuns: MobileInspection[], userId): number => {
      return mobileRuns.filter((item) => item.userId === userId).length;
    },
    [],
  );

  // Function to fetch execution runs and mobile runs
  const fetchData = useCallback(async () => {
    const t = await ExecutionResultService.getRunningCounts().then(
      (res) => res,
    );

    const y = await MobileInspectionService.findAll(
      '?query=testCaseResultId:null,status!' + MobileInspectionStatus.FINISHED,
    ).then((res) => res);
    return [t, y];
  }, []);

  // Function to handle reserved pool scenario
  const handleReservedPool = useCallback(
    (
      executionRuns: ExecutionResult[],
      mobileRuns: MobileInspection[],
      user: User,
      existingRuns: number,
      setShowNoParall,
      redirection,
    ) => {
      const userExecutionSessions = executionRuns.filter(
        (item) => item.executedById === user.id,
      );
      const userReservedPoolRuns = getDryCount(userExecutionSessions);
      const reservedPoolDryCount = getDryCount(
        executionRuns.filter((item) => item.executedById !== user.id),
      );
      const reservedPoolMobileRuns = getMobileRunsCount(mobileRuns, user.id);

      const reservedPoolExistingRuns =
        existingRuns - reservedPoolDryCount - reservedPoolMobileRuns;

      if (reservedPoolExistingRuns > user.maxParallelRuns) {
        setShowNoParall(true);
      } else {
        navigate(redirection);
      }
    },
    [],
  );

  // Function to handle common pool scenario
  const handleCommonPool = useCallback(
    (
      user: User,
      existingRuns: number,
      setShowNoParall,
      redirection,
      executionRuns,
      mobileRuns,
    ) => {
      if (
        (executionRuns?.length || 0) + (mobileRuns?.length || 0) >=
        user.availableCommonPoolRuns
      ) {
        setShowNoParall(true);
      } else {
        navigate(redirection);
      }
    },
    [],
  );

  // Main function to execute the code
  const executeCode = useCallback(
    (setShowNoParall, redirection, setRunningTestplan, setInspections) => {
      let existingRuns = 0;
      fetchData().then((item) => {
        const executionRuns = item[0]?.content;
        const mobileRuns = item[1]?.content;
        setRunningTestplan(executionRuns?.length);
        setInspections(mobileRuns);
        existingRuns = getDryCount(executionRuns);

        // const user = session.user;

        if (session?.user) {
          UserService.show(session?.user?.id).then((resp) => {
            const userResponse = resp;
            if (userResponse.licensePoolType === 'RESERVED_POOL') {
              handleReservedPool(
                executionRuns,
                mobileRuns,
                userResponse,
                existingRuns,
                setShowNoParall,
                redirection,
              );
            } else {
              UserService.findAll(
                'licensePoolType:' + LicensePoolType.RESERVED_POOL,
              ).then((response) => {
                if (response.content.length > 0) {
                  response.content.forEach((user) => {
                    handleCommonPool(
                      userResponse,
                      existingRuns,
                      setShowNoParall,
                      redirection,
                      executionRuns,
                      mobileRuns,
                    );
                  });
                }
                handleCommonPool(
                  userResponse,
                  existingRuns,
                  setShowNoParall,
                  redirection,
                  executionRuns,
                  mobileRuns,
                );
              });
            }
          });
        }
      });
    },
    [],
  );

  return {
    testplanItemGenerator,
    mobileInspactionGenerator,
    debuggingSessionGenerator,
    executeCode,
  };
};
